import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Dialog, Layout, ListItem, Select, TextArea, TextField} from '@phonectas/unifon-ui-kit';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';
import TextWithLabel from '../TextWithLabel';
import {isValidPhone} from 'utils/validators';
import MessageLength from '../MessageLength';
import {phoneNumberWithoutSpaces} from 'utils/number';

interface ByttDialogProps {
    show: boolean;
    onClose: () => void;
}

interface TemplateType {
    number: string;
    text: string;
}

const ByttDialog: React.FC<ByttDialogProps> = ({show, onClose}) => {
	const [t] = useTranslation();
	const {
		sendSms,
		getConfigTemplates,
		getAccountFeed,
	} = useThreeSixtyNewStore((state) => ({
		sendSms: state.sendSms,
		getConfigTemplates: state.getConfigTemplates,
		getAccountFeed: state.getAccountFeed,
	}), shallow);
	const {
		byttDetails,
		account,
		caller,
		eventFilter,
	} = useThreeSixtyNewStore((state) => ({
		byttDetails: state.byttDetails,
		account: state.activeAccount,
		caller: state.bnRedirect.user?.phone_number,
		eventFilter: state?.pageFilters?.eventFeed,
	}), shallow);

	const defaultTemplate = useMemo(() => {
		return {
			number: '',
			text: '',
		};
	}, []);

	const [smsLanguage, setSmsLanguage] = useState<'NO' | 'EN'>('NO');
	const [temp, setTemp] = useState<TemplateType>(defaultTemplate);

	const handleSmsLanguageChange = (val: string) => {
		setSmsLanguage(val === 'EN' ? 'EN' : 'NO');
		const langKey = val === 'EN' ? 'en' : 'no';
		let templateText = '';
		if (
			Array.isArray(byttDetails?.lang) &&
        byttDetails?.lang.length > 0 &&
        byttDetails.lang[0]?.[langKey]
		) {
			templateText = byttDetails.lang[0][langKey];
		}
		setTemp((prev) => ({
			...prev,
			text: templateText,
		}));
	};

	const handleChanges = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setTemp((prev) => ({
			...prev,
			[e.target.ariaLabel as keyof TemplateType]: e.target.value,
		}));
	}, []);

	const handleSendSms = useCallback(() => {
		if (byttDetails) {
			sendSms({
				number: temp.number,
				text: temp.text,
				onSuccess: () => {
					setTemp(defaultTemplate);
					if (account?.id) {
						getAccountFeed({id: account.id, feedType: eventFilter ?? []});
					}
					onClose();
				},
			});
		}
	}, [
		temp,
		sendSms,
		byttDetails,
		defaultTemplate,
		onClose,
		account,
		eventFilter,
		getAccountFeed,
	]);

	const isValidPhoneNumber = useCallback((val: string | number) => {
		if (typeof val === 'number') val = val.toString();
		return val !== '' && isValidPhone(val);
	}, []);

	const canSendSms = useMemo(() => {
		if (byttDetails) {
			return (
				temp.text &&
                isValidPhone(temp.number)
			);
		}
		return false;
	}, [temp, byttDetails]);

	useEffect(() => {
		if (show && byttDetails === null) {
			getConfigTemplates('bytt_sms');
		}
	}, [show, getConfigTemplates, byttDetails]);

	useEffect(() => {
		if (byttDetails) {
			const callerNumber = caller ? String(caller) : '';
			const langKey = smsLanguage === 'EN' ? 'en' : 'no';
			let templateText = '';
			if (
				Array.isArray(byttDetails?.lang) &&
                byttDetails?.lang.length > 0 &&
                byttDetails.lang[0]?.[langKey]
			) {
				templateText = byttDetails.lang[0][langKey];
			}
			setTemp((prev) => ({
				...prev,
				text: templateText,
				number: callerNumber ? phoneNumberWithoutSpaces(callerNumber) : '',
			}));
		}
	}, [byttDetails, smsLanguage, caller]);

	return (
		<Dialog
			show={show}
			onClose={onClose}
			id="trustpilot-dialog"
			title="Bytt.no"
			onOk={handleSendSms}
			disableOk={!canSendSms}
		>
			<Layout direction="column" nowrap fullWidth>
				<TextWithLabel label={t('Customer')} text={account?.account_details?.name || ''} />
				<Layout nowrap fullWidth>
					<Layout direction="column" nowrap fullWidth>
						<TextField
							aria-label="number"
							value={temp.number}
							label={t('Phone')}
							onChange={handleChanges}
							fullWidth
							validator={isValidPhoneNumber}
							errorMessagePlacement="none"
						/>
					</Layout>
					<Layout direction="column" nowrap fullWidth>
						<Select
							aria-label="templateSMS"
							value={smsLanguage}
							label={t('Language')}
							onChange={(val) => {handleSmsLanguageChange(val as string)}}
							fullWidth
						>
							<ListItem key="no" value="NO" primaryText="NO" />
							<ListItem key="en" value="EN" primaryText="EN" />
						</Select>
					</Layout>
				</Layout>
				<Layout direction="column" nowrap fullWidth>
					<Layout direction="row" fullWidth gap="sm">
						<MessageLength message={temp.text} />
					</Layout>
					<Layout direction="row" fullWidth gap="sm">
						<TextArea
							aria-label="text"
							value={temp.text}
							label={t('Text')}
							onChange={handleChanges}
							fullWidth
							style={{height: '200px'}}
						/>
					</Layout>
				</Layout>
			</Layout>
		</Dialog>
	);
}

export default ByttDialog;